import React from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  EmptyState,
  EmptyStateBody,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { FileInvoiceDollarIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';

/**
 * Quotes — an Ancalagon-staff-only Admin page for building price quotes for customers who need
 * bigger or more specific designs. The Manager already knows about products and product prices,
 * so a future feature round will assemble a quote here and send it to the customer.
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types `/admin/quotes` directly
 *      gets <NotFound /> — the same response as a non-existent route, so the admin area's
 *      existence is never leaked. (The backend `/admin` routes enforce this authoritatively;
 *      this is the frontend mirror.)
 */
function Quotes() {
  const auth = useAuth();
  const user = useUserContext();

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Ancalagon-staff-only. Treat anything other than an explicit `true` as "no access" and
  // render the 404 page so the route reveals nothing about the admin area's existence.
  if (user?.is_ancalagon_staff !== true) {
    return <NotFound />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Quotes</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <FileInvoiceDollarIcon />
          </Icon>
          Quotes
        </Content>
        <Content component="p">Build price quotes for customers and send them out</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <EmptyState titleText="Coming soon" icon={FileInvoiceDollarIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Quote management is not yet available.</EmptyStateBody>
        </EmptyState>
      </PageSection>
    </>
  );
}

export { Quotes };
